import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Eye, EyeOff, Mail, Lock, User, Check, Building, Briefcase, MessageCircle, Users, Zap, Crown, X, ChevronRight, Star, Shield, Sparkles } from 'lucide-react';
import { getApiBase } from '../../utils/apiBase';
import Pagamento from '../Pagamento';

interface CadastroProps {
  onSignUp: (userData: { nome: string; email: string; senha: string }) => Promise<void>;
  onCancel: () => void;
  onPaymentSuccess?: (email: string) => void;
  onTrialNeedsVerification?: (email: string, codigo: string, senha: string) => void;
}

type Step = 'dados-pessoais' | 'dados-empresa' | 'plano' | 'pagamento' | 'contato';

const ramosOptions = [
  'Tecnologia',
  'Varejo / Comércio',
  'Indústria',
  'Serviços',
  'Saúde',
  'Educação',
  'Financeiro',
  'Construção Civil',
  'Alimentício',
  'Logística / Transporte',
  'Jurídico',
  'Marketing / Publicidade',
  'Agronegócio',
  'Outro',
];

const comoConheceuOptions = [
  'Google / Pesquisa',
  'Instagram',
  'Facebook',
  'LinkedIn',
  'YouTube',
  'TikTok',
  'X (Twitter)',
  'Indicação de amigo ou colega',
  'Evento ou conferência',
  'Blog ou artigo',
  'Outro',
];

const Cadastro: React.FC<CadastroProps> = ({ onSignUp, onPaymentSuccess, onTrialNeedsVerification }) => {
  const [step, setStep] = useState<Step>('dados-pessoais');
  const [selectedPlan, setSelectedPlan] = useState<'free' | 'pro' | 'enterprise' | null>(null);
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'annual'>('annual');
  const [userCount, setUserCount] = useState(5);

  // Dados pessoais
  const [nome, setNome] = useState('');
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [confirmSenha, setConfirmSenha] = useState('');

  // Dados empresa
  const [nomeEmpresa, setNomeEmpresa] = useState('');
  const [ramo, setRamo] = useState('');
  const [cargo, setCargo] = useState('');
  const [comoConheceu, setComoConheceu] = useState('');
  const [comoConheceuOutro, setComoConheceuOutro] = useState('');

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [aceitouTermos, setAceitouTermos] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const state = location.state as any;
    if (state?.email) {
      setEmail(state.email);
    }
  }, [location]);

  const validateStep1 = () => {
    if (!nome.trim()) { setError('Informe seu nome completo'); return false; }
    if (!email.trim()) { setError('Informe seu email'); return false; }
    if (senha.length < 10) { setError('A senha deve ter pelo menos 10 caracteres'); return false; }
    if (!/[A-Z]/.test(senha)) { setError('A senha deve conter pelo menos uma letra maiúscula'); return false; }
    if (!/[0-9]/.test(senha)) { setError('A senha deve conter pelo menos um número'); return false; }
    if (!/[a-zA-Z]/.test(senha)) { setError('A senha deve conter pelo menos uma letra'); return false; }
    if (senha !== confirmSenha) { setError('As senhas não coincidem'); return false; }
    return true;
  };

  const validateStep2 = () => {
    if (!nomeEmpresa.trim()) { setError('Informe o nome da empresa'); return false; }
    if (!ramo) { setError('Selecione o ramo de atuação'); return false; }
    if (!cargo.trim()) { setError('Informe seu cargo'); return false; }
    return true;
  };

  const [checkingEmail, setCheckingEmail] = useState(false);

  const handleNext = async () => {
    setError('');
    if (step === 'dados-pessoais') {
      if (!validateStep1()) return;
      // Verificar se email já existe na base
      setCheckingEmail(true);
      try {
        const res = await fetch(`${getApiBase()}/verificar-email-existente`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: email.trim() }),
        });
        const data = await res.json();
        if (data.exists) {
          setError('Este email já possui uma conta cadastrada. Faça login ou use outro email.');
          return;
        }
      } catch (err) {
        // Se falhar a verificação, deixa continuar (vai falhar no signup depois)
        console.warn('Não foi possível verificar email:', err);
      } finally {
        setCheckingEmail(false);
      }
      setStep('dados-empresa');
    } else if (step === 'dados-empresa') {
      if (!validateStep2()) return;
      setStep('plano');
    }
  };

  const handleBack = () => {
    setError('');
    if (step === 'dados-empresa') setStep('dados-pessoais');
    else if (step === 'plano') setStep('dados-empresa');
    else if (step === 'pagamento') setStep('plano');
    else if (step === 'contato') setStep('plano');
  };

  const handleSelectPlan = async (plan: 'free' | 'pro' | 'enterprise') => {
    setSelectedPlan(plan);
    if (plan === 'free') {
      // 1. Gerar código de verificação
      const codigo = Math.random().toString(36).substring(2, 8).toUpperCase();

      setLoading(true);
      setError('');
      try {
        // 2. Criar conta trial no backend (fica como 'pendente' até confirmar e-mail)
        const trialRes = await fetch(`${getApiBase()}/pagamento/iniciar-trial`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ nome, email, senha, nomeEmpresa, cargo, codigoVerificacao: codigo }),
        });
        const trialData = await trialRes.json();
        if (!trialRes.ok || !trialData.success) {
          throw new Error(trialData.error || 'Erro ao criar conta trial');
        }

        // 3. Enviar o código por e-mail
        const emailRes = await fetch(`${getApiBase()}/enviar-verificacao`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, nome, codigo }),
        });
        if (!emailRes.ok) {
          throw new Error('Conta criada, mas falha ao enviar e-mail de verificação.');
        }

        // 4. Mostrar tela de verificação
        if (onTrialNeedsVerification) {
          onTrialNeedsVerification(email, codigo, senha);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Erro ao criar conta');
        setSelectedPlan(null);
      } finally {
        setLoading(false);
      }
    } else if (plan === 'pro') {
      // Pro → pagamento
      setStep('pagamento');
    } else {
      // Enterprise → tela de contato
      setStep('contato');
    }
  };

  const handleContatoSubmit = async () => {
    setLoading(true);
    setError('');
    try {
      await onSignUp({ nome, email, senha });
      if (location.state?.targetPath) {
        localStorage.setItem('redirectPath', location.state.targetPath);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao criar conta');
    } finally {
      setLoading(false);
    }
  };

  const stepNumber = step === 'dados-pessoais' ? 1 : step === 'dados-empresa' ? 2 : 3;

  // ── Step indicators ──
  const stepLabels = ['Seus dados', 'Sua empresa', 'Seu plano'];
  const StepIndicator = () => (
    <div className="flex items-center justify-center mb-8">
      {[1, 2, 3].map((s) => (
        <div key={s} className="flex items-center">
          <div className="flex flex-col items-center">
            <div className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-300 ${
              s < stepNumber ? 'bg-indigo-600 text-white shadow-sm' :
              s === stepNumber ? 'bg-indigo-600 text-white shadow-md ring-4 ring-indigo-600/10' :
              'bg-gray-100 text-gray-400'
            }`}>
              {s < stepNumber ? <Check className="w-4 h-4" /> : s}
            </div>
            <span className={`text-[11px] font-medium mt-1.5 whitespace-nowrap transition-colors ${
              s <= stepNumber ? 'text-indigo-600' : 'text-gray-400'
            }`}>
              {stepLabels[s - 1]}
            </span>
          </div>
          {s < 3 && (
            <div className={`w-14 h-0.5 mx-2 mb-5 transition-colors ${s < stepNumber ? 'bg-indigo-600' : 'bg-gray-200'}`} />
          )}
        </div>
      ))}
    </div>
  );

  const inputClass = "w-full pl-10 pr-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 outline-none transition-colors";
  const inputPassClass = "w-full pl-10 pr-12 py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 outline-none transition-colors";
  const selectClass = "w-full pl-10 pr-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 outline-none transition-colors bg-white";

  // ── STEP: Pagamento (Pro) ──
  if (step === 'pagamento') {
    return (
      <div className="min-h-screen bg-gray-50/50 flex items-start justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <div className="text-center sticky top-0 z-10 bg-gray-50/95 backdrop-blur-sm pt-8 pb-4 mb-4 border-b border-gray-200/60 shadow-sm">
            <div className="w-14 h-14 mx-auto rounded-xl bg-indigo-600 text-white flex items-center justify-center font-bold text-xl mb-4">E</div>
            <h1 className="text-2xl font-bold text-gray-900 mb-1">Finalizar pagamento</h1>
            <p className="text-sm text-gray-500">Plano Pro &middot; {userCount} usuário{userCount > 1 ? 's' : ''}</p>
          </div>
          <Pagamento
            userData={{ nome, email, senha, nomeEmpresa, cargo }}
            planData={{ billingPeriod, userCount }}
            onSuccess={(_activated, email) => {
              if (onPaymentSuccess) onPaymentSuccess(email);
            }}
            onBack={handleBack}
            getApiBase={getApiBase}
          />
        </div>
      </div>
    );
  }

  // ── STEP: Contato (Enterprise) ──
  if (step === 'contato') {
    return (
      <div className="min-h-screen bg-gray-50/50 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-lg">
          <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-lg">
            {/* Banner topo */}
            <div className="bg-indigo-600 px-8 pt-10 pb-14 text-center relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-full opacity-10">
                <div className="absolute -top-10 -left-10 w-40 h-40 rounded-full bg-white" />
                <div className="absolute -bottom-20 -right-10 w-56 h-56 rounded-full bg-white" />
                <div className="absolute top-6 right-16 w-16 h-16 rounded-full bg-white" />
              </div>
              <div className="absolute top-5 left-8 opacity-20"><Star className="w-5 h-5 text-white" /></div>
              <div className="absolute top-12 right-12 opacity-20"><Sparkles className="w-6 h-6 text-white" /></div>
              <div className="absolute bottom-8 left-16 opacity-15"><Shield className="w-5 h-5 text-white" /></div>
              <div className="relative z-10">
                <div className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-bold mb-4 bg-white/20 text-white border border-white/20">
                  <Users className="w-3.5 h-3.5" /> Plano Enterprise
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">Excelente escolha! 🎉</h2>
                <p className="text-indigo-200 text-sm max-w-xs mx-auto">
                  Nossa equipe entrará em contato para apresentar uma proposta personalizada
                </p>
              </div>
            </div>

            {/* Conteúdo */}
            <div className="px-8 -mt-8 pb-8 relative z-10">
              <div className="grid grid-cols-2 gap-3 mb-6">
                <div className="bg-white rounded-xl border border-gray-200 shadow-md p-4 text-center">
                  <div className="w-10 h-10 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-2">
                    <Check className="w-5 h-5 text-green-600" />
                  </div>
                  <p className="text-xs font-semibold text-gray-900">Retorno em 24h</p>
                  <p className="text-[10px] text-gray-500 mt-0.5">Dias úteis</p>
                </div>
                <div className="bg-white rounded-xl border border-gray-200 shadow-md p-4 text-center">
                  <div className="w-10 h-10 rounded-full bg-indigo-50 flex items-center justify-center mx-auto mb-2">
                    <Shield className="w-5 h-5 text-indigo-600" />
                  </div>
                  <p className="text-xs font-semibold text-gray-900">Setup gratuito</p>
                  <p className="text-[10px] text-gray-500 mt-0.5">Sem taxa adicional</p>
                </div>
              </div>

              {/* Dados do usuário */}
              <div className="mb-6">
                <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-3">Dados da conta</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  {[
                    { icon: User, label: 'Nome', value: nome },
                    { icon: Mail, label: 'Email', value: email },
                    { icon: Building, label: 'Empresa', value: nomeEmpresa },
                    ...(cargo ? [{ icon: Briefcase, label: 'Cargo', value: cargo }] : []),
                  ].map(({ icon: Icon, label, value }) => (
                    <div key={label} className="flex items-center gap-3 bg-gray-50 rounded-lg px-4 py-3">
                      <Icon className="w-4 h-4 text-indigo-500 shrink-0" />
                      <div className="min-w-0">
                        <p className="text-[10px] text-gray-400 uppercase tracking-wide">{label}</p>
                        <p className="text-sm text-gray-800 font-medium truncate">{value}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {error && (
                <div className="mb-4 p-3 text-sm text-red-700 rounded-lg bg-red-50 border border-red-200">{error}</div>
              )}

              <button
                onClick={handleContatoSubmit}
                disabled={loading}
                className="w-full py-3.5 px-4 rounded-xl text-sm font-bold text-white bg-indigo-600 hover:bg-indigo-700 transition-all shadow-sm hover:shadow-md disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {loading ? 'Enviando...' : (<><Sparkles className="w-4 h-4" /> Enviar solicitação Enterprise</>)}
              </button>
              <button
                onClick={handleBack}
                className="mt-4 w-full text-sm text-gray-500 hover:text-gray-900 font-medium transition-colors flex items-center justify-center gap-1.5"
              >
                <ArrowLeft className="w-4 h-4" />
                Voltar e escolher outro plano
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-gray-50/50 flex ${step === 'plano' ? 'items-start' : 'items-center'} justify-center px-4 ${step === 'plano' ? 'py-0' : 'py-12'}`}>
      <div className={`w-full ${step === 'plano' ? 'max-w-4xl' : 'max-w-md'}`}>
        <div className={`text-center ${step === 'plano' ? 'sticky top-0 z-10 bg-gray-50/95 backdrop-blur-sm pt-8 pb-4 mb-4 border-b border-gray-200/60 shadow-sm' : 'mb-8'}`}>
          <div className="w-14 h-14 mx-auto rounded-xl bg-indigo-600 text-white flex items-center justify-center font-bold text-xl mb-4">E</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-1">
            {step === 'dados-pessoais' && 'Criar sua conta'}
            {step === 'dados-empresa' && 'Sobre sua empresa'}
            {step === 'plano' && 'Escolha seu plano'}
          </h1>
          <p className="text-sm text-gray-500">
            {step === 'dados-pessoais' && 'Junte-se ao Escalone e organize seus fluxos de trabalho'}
            {step === 'dados-empresa' && 'Nos conte um pouco sobre onde você trabalha'}
            {step === 'plano' && 'Selecione o plano ideal para sua equipe'}
          </p>
          {location.state?.message && step === 'dados-pessoais' && (
            <p className="mt-3 text-sm text-blue-600 bg-blue-50 p-3 rounded-lg border border-blue-200">{location.state.message}</p>
          )}
          {step === 'plano' && <div className="mt-4"><StepIndicator /></div>}
        </div>

        {step !== 'plano' && <StepIndicator />}

        {/* ── STEP 1: Dados Pessoais ── */}
        {step === 'dados-pessoais' && (
          <div className="bg-white p-8 rounded-2xl border border-gray-200 shadow-sm">
            {error && (
              <div className="mb-5 p-3 text-sm text-red-700 rounded-lg bg-red-50 border border-red-200">{error}</div>
            )}
            <div className="space-y-5">
              <div>
                <label htmlFor="nome" className="block text-sm font-medium text-gray-700 mb-1.5">Nome completo</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input id="nome" type="text" required value={nome} onChange={(e) => setNome(e.target.value)}
                    className={inputClass} placeholder="Digite seu nome completo" />
                </div>
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1.5">Email</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                    readOnly={!!location.state?.email}
                    className={inputClass} placeholder="Digite seu email" />
                </div>
              </div>

              <div>
                <label htmlFor="senha" className="block text-sm font-medium text-gray-700 mb-1.5">Senha</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input id="senha" type={showPassword ? "text" : "password"} required value={senha}
                    onChange={(e) => setSenha(e.target.value)}
                    className={inputPassClass} placeholder="Digite sua senha" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors">
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <p className="mt-1.5 text-xs text-gray-500 flex items-center gap-1">
                  <Check className="w-3 h-3 text-green-500" /> Mín. 10 caracteres, uma maiúscula e um número
                </p>
              </div>

              <div>
                <label htmlFor="confirmSenha" className="block text-sm font-medium text-gray-700 mb-1.5">Confirmar senha</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input id="confirmSenha" type={showConfirmPassword ? "text" : "password"} required value={confirmSenha}
                    onChange={(e) => setConfirmSenha(e.target.value)}
                    className={inputPassClass} placeholder="Confirme sua senha" />
                  <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors">
                    {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <button onClick={handleNext}
                disabled={checkingEmail}
                className="w-full py-3 px-4 rounded-xl text-sm font-bold text-white bg-indigo-600 hover:bg-indigo-700 transition-all shadow-sm hover:shadow-md flex items-center justify-center gap-2 disabled:opacity-50">
                {checkingEmail ? 'Verificando...' : (<>Próximo <ArrowRight className="w-4 h-4" /></>)}
              </button>

              <div className="text-center">
                <button type="button" onClick={() => navigate('/login')}
                  className="text-sm text-gray-500 hover:text-gray-900 font-medium transition-colors">
                  Já tem uma conta? Faça login
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── STEP 2: Dados da Empresa ── */}
        {step === 'dados-empresa' && (
          <div className="bg-white p-8 rounded-2xl border border-gray-200 shadow-sm">
            {error && (
              <div className="mb-5 p-3 text-sm text-red-700 rounded-lg bg-red-50 border border-red-200">{error}</div>
            )}
            <div className="space-y-5">
              <div>
                <label htmlFor="nomeEmpresa" className="block text-sm font-medium text-gray-700 mb-1.5">Nome da empresa</label>
                <div className="relative">
                  <Building className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input id="nomeEmpresa" type="text" required value={nomeEmpresa}
                    onChange={(e) => setNomeEmpresa(e.target.value)}
                    className={inputClass} placeholder="Nome da sua empresa" />
                </div>
              </div>

              <div>
                <label htmlFor="ramo" className="block text-sm font-medium text-gray-700 mb-1.5">Ramo de atuação</label>
                <div className="relative">
                  <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <select id="ramo" value={ramo} onChange={(e) => setRamo(e.target.value)}
                    className={selectClass}>
                    <option value="">Selecione o ramo</option>
                    {ramosOptions.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <label htmlFor="cargo" className="block text-sm font-medium text-gray-700 mb-1.5">Seu cargo</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input id="cargo" type="text" required value={cargo}
                    onChange={(e) => setCargo(e.target.value)}
                    className={inputClass} placeholder="Ex: Gerente, Diretor, Analista..." />
                </div>
              </div>

              <div>
                <label htmlFor="comoConheceu" className="block text-sm font-medium text-gray-700 mb-1.5">
                  Como conheceu o Escalone? <span className="text-gray-400 font-normal">(opcional)</span>
                </label>
                <div className="relative">
                  <MessageCircle className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <select id="comoConheceu" value={comoConheceu} onChange={(e) => { setComoConheceu(e.target.value); if (e.target.value !== 'Outro') setComoConheceuOutro(''); }}
                    className={selectClass}>
                    <option value="">Selecione uma opção</option>
                    {comoConheceuOptions.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                {comoConheceu === 'Outro' && (
                  <div className="relative mt-2">
                    <MessageCircle className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input type="text" value={comoConheceuOutro} onChange={(e) => setComoConheceuOutro(e.target.value)}
                      className={inputClass} placeholder="Conte-nos como conheceu o Escalone" />
                  </div>
                )}
              </div>

              <div className="flex gap-3">
                <button onClick={handleBack}
                  className="flex-1 py-3 px-4 rounded-xl text-sm font-semibold text-gray-700 bg-gray-50 hover:bg-gray-100 transition-all border border-gray-200 flex items-center justify-center gap-2">
                  <ArrowLeft className="w-4 h-4" /> Voltar
                </button>
                <button onClick={handleNext}
                  className="flex-1 py-3 px-4 rounded-xl text-sm font-bold text-white bg-indigo-600 hover:bg-indigo-700 transition-all shadow-sm hover:shadow-md flex items-center justify-center gap-2">
                  Próximo <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── STEP 3: Planos ── */}
        {step === 'plano' && (
          <>
            {error && (
              <div className="mb-5 p-3 text-sm text-red-700 rounded-lg bg-red-50 border border-red-200 text-center">{error}</div>
            )}

            {/* Termos de uso */}
            <div className="mb-6 max-w-md mx-auto">
              <label className="flex items-start gap-3 cursor-pointer group select-none">
                <input
                  type="checkbox"
                  checked={aceitouTermos}
                  onChange={(e) => setAceitouTermos(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                />
                <span className="text-sm text-gray-600 leading-relaxed">
                  Li e aceito os{' '}
                  <button
                    type="button"
                    onClick={(e) => { e.preventDefault(); window.open('/termos', '_blank'); }}
                    className="text-indigo-600 hover:text-indigo-800 underline font-medium"
                  >
                    Termos de Uso e Política de Privacidade
                  </button>
                  {' '}do Escalone.
                </span>
              </label>
              {!aceitouTermos && (
                <p className="text-xs text-amber-600 mt-2 ml-7">Aceite os termos para continuar.</p>
              )}
            </div>

            {/* Toggle Mensal / Anual */}
            <div className="flex items-center justify-center mb-8">
              <div className="bg-gray-100 rounded-full p-1 inline-flex items-center relative">
                <button
                  onClick={() => setBillingPeriod('monthly')}
                  className={`px-5 py-2 rounded-full text-sm font-semibold transition-all duration-200 relative z-10 ${
                    billingPeriod === 'monthly'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Mensal
                </button>
                <button
                  onClick={() => setBillingPeriod('annual')}
                  className={`px-5 py-2 rounded-full text-sm font-semibold transition-all duration-200 relative z-10 flex items-center gap-1.5 ${
                    billingPeriod === 'annual'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Anual
                  <span className="bg-emerald-100 text-emerald-700 text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                    -10%
                  </span>
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 items-stretch">
              {/* Free */}
              <div className="bg-white rounded-2xl border border-gray-200 p-7 flex flex-col hover:shadow-lg transition-all duration-300 group">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-11 h-11 rounded-xl bg-emerald-50 flex items-center justify-center group-hover:scale-105 transition-transform">
                    <Zap className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">Free</h3>
                    <p className="text-xs text-gray-500">Para conhecer a plataforma</p>
                  </div>
                </div>

                <div className="mb-4 pb-4 border-b border-gray-100">
                  <span className="text-4xl font-extrabold text-gray-900">R$ 0</span>
                  <p className="text-sm text-gray-500 mt-1.5">7 dias grátis · Sem cartão</p>
                </div>

                <p className="text-xs text-emerald-600 font-medium bg-emerald-50 rounded-xl px-4 py-2.5 mb-5 leading-relaxed">
                  Ficou curioso? Experimente nosso sistema sem compromisso e veja como ele pode transformar seus processos. 🚀
                </p>

                <ul className="space-y-3 mb-7 flex-1">
                  {['7 dias de acesso completo', 'Até 5 fluxos de trabalho', 'Até 10 colaboradores', 'Até 10 automações por fluxo'].map((item) => (
                    <li key={item} className="flex items-start gap-2.5 text-sm text-gray-600">
                      <div className="w-5 h-5 rounded-full bg-emerald-50 flex items-center justify-center shrink-0 mt-0.5">
                        <Check className="w-3 h-3 text-emerald-600" />
                      </div>
                      <span>{item}</span>
                    </li>
                  ))}
                  {['Automações limitadas', 'Anexos limitados'].map((item) => (
                    <li key={item} className="flex items-start gap-2.5 text-sm text-gray-400">
                      <div className="w-5 h-5 rounded-full bg-gray-50 flex items-center justify-center shrink-0 mt-0.5">
                        <X className="w-3 h-3 text-gray-300" />
                      </div>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => handleSelectPlan('free')}
                  disabled={loading || !aceitouTermos}
                  className="w-full py-3 px-4 rounded-xl text-sm font-semibold text-gray-700 bg-gray-50 hover:bg-gray-100 transition-all border border-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  title={!aceitouTermos ? 'Aceite os termos de uso para continuar' : ''}
                >
                  {loading && selectedPlan === 'free' ? 'Criando conta...' : 'Começar grátis'}
                </button>
              </div>

              {/* Pro — destaque */}
              <div className="bg-white rounded-2xl border-2 border-indigo-600 p-7 flex flex-col relative shadow-xl shadow-indigo-500/10">
                <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                  <span className="bg-indigo-600 text-white text-xs font-bold px-4 py-1.5 rounded-full shadow-sm inline-flex items-center gap-1">
                    <Crown className="w-3 h-3" /> Recomendado
                  </span>
                </div>

                <div className="flex items-center gap-3 mb-5 mt-1">
                  <div className="w-11 h-11 rounded-xl bg-indigo-100 flex items-center justify-center">
                    <Crown className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">Pro</h3>
                    <p className="text-xs text-gray-500">Para equipes em crescimento</p>
                  </div>
                </div>

                <div className="mb-5 pb-5 border-b border-indigo-100">
                  {billingPeriod === 'monthly' ? (
                    <>
                      <div className="flex items-baseline gap-1">
                        <span className="text-4xl font-extrabold text-gray-900">R$ 80</span>
                        <span className="text-sm text-gray-500">/ usuário / mês</span>
                      </div>
                      <p className="text-sm text-indigo-600 font-medium mt-1.5">Cobrança mensal</p>
                    </>
                  ) : (
                    <>
                      <div className="flex items-baseline gap-1">
                        <span className="text-4xl font-extrabold text-gray-900">R$ 72</span>
                        <span className="text-sm text-gray-500">/ usuário / mês</span>
                      </div>
                      <div className="flex items-center gap-2 mt-1.5">
                        <span className="text-sm text-gray-400 line-through">R$ 80</span>
                        <span className="bg-emerald-100 text-emerald-700 text-xs font-bold px-2 py-0.5 rounded-full">
                          Economize 10%
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">R$ 864/usuário cobrado anualmente</p>
                    </>
                  )}
                </div>

                {/* Simulador de usuários */}
                <div className="mb-5 pb-5 border-b border-indigo-100">
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 block">Simule seu investimento</label>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => setUserCount(Math.max(1, userCount - 1))}
                      className="w-8 h-8 rounded-lg bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-600 font-bold transition-colors text-lg"
                    >−</button>
                    <div className="flex-1 text-center">
                      <input
                        type="number"
                        min={1}
                        max={999}
                        value={userCount}
                        onChange={(e) => setUserCount(Math.max(1, Math.min(999, parseInt(e.target.value) || 1)))}
                        className="w-16 text-center text-2xl font-extrabold text-gray-900 border-0 bg-transparent outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      />
                      <p className="text-[11px] text-gray-500 -mt-0.5">usuários</p>
                    </div>
                    <button
                      onClick={() => setUserCount(Math.min(999, userCount + 1))}
                      className="w-8 h-8 rounded-lg bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-600 font-bold transition-colors text-lg"
                    >+</button>
                  </div>
                  <div className="bg-indigo-50 rounded-xl p-4 mt-3">
                    {billingPeriod === 'monthly' ? (
                      <div className="text-center">
                        <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Total estimado mensal</p>
                        <p className="text-3xl font-extrabold text-indigo-600 mt-1">
                          R$ {(userCount * 80).toLocaleString('pt-BR')}
                          <span className="text-sm font-semibold text-gray-500">/mês</span>
                        </p>
                      </div>
                    ) : (
                      <div className="text-center">
                        <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Total estimado anual</p>
                        <p className="text-3xl font-extrabold text-indigo-600 mt-1">
                          R$ {(userCount * 864).toLocaleString('pt-BR')}
                          <span className="text-sm font-semibold text-gray-500">/ano</span>
                        </p>
                        <div className="flex items-center justify-center gap-3 mt-2 pt-2 border-t border-indigo-100">
                          <div>
                            <p className="text-[10px] text-gray-400 uppercase">Por mês</p>
                            <p className="text-sm font-bold text-gray-700">R$ {(userCount * 72).toLocaleString('pt-BR')}</p>
                          </div>
                          <div className="w-px h-7 bg-indigo-200" />
                          <div>
                            <p className="text-[10px] text-gray-400 uppercase">Economia</p>
                            <p className="text-sm font-bold text-emerald-600">R$ {(userCount * 96).toLocaleString('pt-BR')}/ano</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <ul className="space-y-3 mb-7 flex-1">
                  {[
                    { text: 'Empresas ilimitadas', bold: true },
                    { text: 'Usuários ilimitados', bold: true },
                    { text: 'Fluxos ilimitados', bold: true },
                    { text: 'Automações completas', bold: false },
                    { text: 'Anexos ilimitados', bold: false },
                  ].map(({ text, bold }) => (
                    <li key={text} className="flex items-start gap-2.5 text-sm text-gray-700">
                      <div className="w-5 h-5 rounded-full bg-indigo-50 flex items-center justify-center shrink-0 mt-0.5">
                        <Check className="w-3 h-3 text-indigo-600" />
                      </div>
                      <span className={bold ? 'font-semibold' : ''}>{text}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => handleSelectPlan('pro')}
                  disabled={!aceitouTermos}
                  className="w-full py-3 px-4 rounded-xl text-sm font-bold text-white bg-indigo-600 hover:bg-indigo-700 transition-all shadow-sm hover:shadow-md flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  title={!aceitouTermos ? 'Aceite os termos de uso para continuar' : ''}
                >
                  Escolher Pro <ChevronRight className="w-4 h-4" />
                </button>
              </div>

              {/* Enterprise */}
              <div className="relative bg-white rounded-2xl border border-gray-200 p-7 flex flex-col hover:shadow-lg transition-all duration-300 group h-full">
                <div className="absolute top-4 right-4">
                  <Sparkles className="w-5 h-5 text-indigo-300" />
                </div>

                <div className="flex items-center gap-3 mb-3">
                  <div className="w-11 h-11 rounded-xl bg-indigo-50 flex items-center justify-center group-hover:scale-105 transition-transform">
                    <Crown className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">Enterprise</h3>
                    <p className="text-xs text-gray-500">Para grandes operações</p>
                  </div>
                </div>

                <p className="text-xs text-gray-500 mb-3 leading-relaxed">
                  Ideal para empresas com <span className="font-semibold text-indigo-600">20+ colaboradores</span>, múltiplos processos e necessidade de integrações avançadas.
                </p>
                <p className="text-xs text-indigo-600 font-semibold mb-5 leading-relaxed">
                  Desenvolvemos o seu processo do zero — do fluxo à integração.
                </p>

                <div className="mb-5 pb-5 border-b border-gray-100">
                  <span className="text-4xl font-extrabold text-gray-900">Custom</span>
                  <p className="text-sm text-gray-500 mt-1.5">Preço sob consulta</p>
                </div>

                <ul className="space-y-3 mb-7 flex-1">
                  {[
                    'Tudo do plano Pro',
                    'Consultoria dedicada',
                    'Integração com ERPs, CRMs, TMS e muito mais...',
                    'Múltiplos fluxos e processos',
                    'Onboarding personalizado',
                    'Suporte prioritário 24/7',
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-2.5 text-sm text-gray-700">
                      <div className="w-5 h-5 rounded-full bg-indigo-50 flex items-center justify-center shrink-0 mt-0.5">
                        <Check className="w-3 h-3 text-indigo-600" />
                      </div>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => handleSelectPlan('enterprise')}
                  disabled={!aceitouTermos}
                  className="w-full py-3 px-4 rounded-xl text-sm font-bold text-white bg-indigo-600 hover:bg-indigo-700 transition-all shadow-sm hover:shadow-md flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  title={!aceitouTermos ? 'Aceite os termos de uso para continuar' : ''}
                >
                  Falar com nosso time <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>

            <p className="text-center text-xs text-gray-400 mt-5">
              Todos os planos incluem suporte por email e atualizações gratuitas.
            </p>

            <div className="text-center mt-5 pb-12">
              <button onClick={handleBack}
                className="text-sm text-gray-500 hover:text-gray-900 font-medium transition-colors flex items-center justify-center gap-1.5 mx-auto">
                <ArrowLeft className="w-4 h-4" /> Voltar para dados da empresa
              </button>
            </div>
          </>
        )}

        {step !== 'plano' && (
          <div className="text-center text-sm mt-6">
            <button type="button" onClick={() => navigate('/')}
              className="text-indigo-600 hover:text-indigo-700 font-semibold hover:underline transition-colors">
              Conhecer melhor o Escalone
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Cadastro;